import { FileBarChart, Target, RefreshCw, Edit3, Check, X, Trophy, Medal, Award } from "lucide-react";
import PropTypes from "prop-types";

const DealerReportView = ({
  sellerReport, loadingReport, fetchSellerReport,
  globalTarget, editingGlobalTarget, setEditingGlobalTarget,
  tempGlobalTarget, setTempGlobalTarget, saveGlobalTarget,
  editingSellerTarget, setEditingSellerTarget,
  tempSellerTarget, setTempSellerTarget, saveSellerTarget,
  leaderboard, fetchLeaderboard, t
}) => (
  <div className="animate-fade-in">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-bold text-white" data-testid="report-title">{t.sellersReport}</h2>
      <button onClick={() => fetchSellerReport()} className="btn-lojack-outline text-sm flex items-center gap-2"><RefreshCw size={14} /> {t.refresh}</button>
    </div>

    {loadingReport ? (
      <div className="wallet-card text-center py-8"><div className="spinner-small mx-auto mb-3" /><p className="text-gray-500">{t.loadingReport}</p></div>
    ) : sellerReport ? (
      <>
        {/* Global Target Config */}
        <div className="wallet-card mb-4 flex items-center justify-between" data-testid="global-target-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-lojack-red/10 border border-lojack-red/20 flex items-center justify-center">
              <Target size={16} className="text-lojack-red" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{t.globalTarget}</p>
              <p className="text-gray-500 text-xs">{t.globalTargetDesc}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editingGlobalTarget ? (
              <>
                <input type="number" min="0" value={tempGlobalTarget} onChange={e => setTempGlobalTarget(e.target.value)}
                  className="w-20 px-3 py-1.5 bg-[#111] border border-gray-700 rounded-lg text-white text-center text-sm focus:border-lojack-red focus:outline-none"
                  onKeyDown={e => e.key === 'Enter' && saveGlobalTarget()} autoFocus data-testid="global-target-input" />
                <button onClick={saveGlobalTarget} className="p-1.5 rounded-lg bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20 transition-colors" data-testid="global-target-save">
                  <Check size={14} className="text-lojack-blue-400" />
                </button>
                <button onClick={() => setEditingGlobalTarget(false)} className="p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">
                  <X size={14} className="text-gray-400" />
                </button>
              </>
            ) : (
              <>
                <span className="text-white font-bold text-lg tabular-nums" data-testid="global-target-value">{globalTarget || '-'}</span>
                <span className="text-gray-500 text-xs">{t.clientsMonth}</span>
                <button onClick={() => { setTempGlobalTarget(String(globalTarget)); setEditingGlobalTarget(true); }}
                  className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-testid="global-target-edit">
                  <Edit3 size={14} className="text-gray-400" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="wallet-card text-center">
            <p className="text-2xl font-bold text-white tabular-nums">{sellerReport.total_customers}</p>
            <p className="text-gray-500 text-xs mt-1">{t.totalCustomers}</p>
          </div>
          <div className="wallet-card text-center">
            <p className="text-2xl font-bold text-lojack-blue-400 tabular-nums">{sellerReport.total_contracts}</p>
            <p className="text-gray-500 text-xs mt-1">{t.contractsSigned}</p>
          </div>
          <div className="wallet-card text-center">
            <p className="text-2xl font-bold text-blue-400 tabular-nums">{sellerReport.direct_customers}</p>
            <p className="text-gray-500 text-xs mt-1">{t.directCustomers}</p>
          </div>
          <div className="wallet-card text-center">
            <p className="text-2xl font-bold text-purple-400 tabular-nums">{sellerReport.sellers?.length || 0}</p>
            <p className="text-gray-500 text-xs mt-1">{t.sellerCount}</p>
          </div>
        </div>

        {/* Per-Seller Table with Progress Bars */}
        {sellerReport.sellers && sellerReport.sellers.length > 0 ? (
          <div className="wallet-card overflow-x-auto">
            <h3 className="text-md font-semibold text-white mb-4 flex items-center gap-2"><FileBarChart size={16} className="text-lojack-red" /> {t.performanceBySeller}</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 pb-3 font-medium">{t.sellers}</th>
                  <th className="text-center text-gray-400 pb-3 font-medium">{t.target}</th>
                  <th className="text-center text-gray-400 pb-3 font-medium" style={{minWidth:'140px'}}>{t.monthProgress}</th>
                  <th className="text-center text-gray-400 pb-3 font-medium">{t.total}</th>
                  <th className="text-center text-gray-400 pb-3 font-medium">{t.contracts}</th>
                  <th className="text-center text-gray-400 pb-3 font-medium">7gg</th>
                  <th className="text-center text-gray-400 pb-3 font-medium">30gg</th>
                </tr>
              </thead>
              <tbody>
                {sellerReport.sellers.map(s => {
                  const target = s.monthly_target || 0;
                  const progress = s.monthly_progress || 0;
                  const pct = target > 0 ? Math.min(100, Math.round((progress / target) * 100)) : 0;
                  const barColor = pct >= 100 ? 'bg-lojack-blue-500' : pct >= 70 ? 'bg-lojack-clause-500' : 'bg-lojack-red';
                  return (
                    <tr key={s.id} className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors" data-testid={`report-row-${s.id}`}>
                      <td className="py-3">
                        <p className="text-white font-medium">{s.first_name} {s.last_name}</p>
                        <p className="text-gray-500 text-xs">{s.email}{s.department ? ` | ${s.department}` : ''}</p>
                      </td>
                      <td className="text-center py-3">
                        {editingSellerTarget === s.id ? (
                          <div className="flex items-center justify-center gap-1">
                            <input type="number" min="0" value={tempSellerTarget} onChange={e => setTempSellerTarget(e.target.value)}
                              className="w-14 px-2 py-1 bg-[#111] border border-gray-700 rounded text-white text-center text-xs focus:border-lojack-red focus:outline-none"
                              placeholder={String(globalTarget)} onKeyDown={e => e.key === 'Enter' && saveSellerTarget(s.id)} autoFocus data-testid={`seller-target-input-${s.id}`} />
                            <button onClick={() => saveSellerTarget(s.id)} className="p-1 rounded bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20">
                              <Check size={10} className="text-lojack-blue-400" />
                            </button>
                            <button onClick={() => setEditingSellerTarget(null)} className="p-1 rounded bg-gray-800 hover:bg-gray-700">
                              <X size={10} className="text-gray-400" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditingSellerTarget(s.id); setTempSellerTarget(s.monthly_target !== globalTarget && s.monthly_target ? String(s.monthly_target) : ''); }}
                            className="text-white tabular-nums hover:text-lojack-red transition-colors group" data-testid={`seller-target-edit-${s.id}`}>
                            {target > 0 ? target : '-'}
                            <Edit3 size={10} className="inline ml-1 text-gray-600 group-hover:text-lojack-red" />
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {target > 0 ? (
                          <div data-testid={`seller-progress-${s.id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-bold tabular-nums ${pct >= 100 ? 'text-lojack-blue-400' : 'text-white'}`}>{progress}/{target}</span>
                              <span className={`text-xs font-bold tabular-nums ${pct >= 100 ? 'text-lojack-blue-400' : pct >= 70 ? 'text-lojack-clause-400' : 'text-gray-400'}`}>{pct}%</span>
                            </div>
                            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                              <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{width: `${pct}%`}} />
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">{t.noTarget}</span>
                        )}
                      </td>
                      <td className="text-center text-white font-semibold tabular-nums">{s.total_customers}</td>
                      <td className="text-center text-lojack-blue-400 tabular-nums">{s.contracts_signed}</td>
                      <td className="text-center text-gray-300 tabular-nums">{s.last_7_days}</td>
                      <td className="text-center text-gray-300 tabular-nums">{s.last_30_days}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="wallet-card text-center py-8">
            <FileBarChart size={40} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">{t.noSellersReport}</p>
          </div>
        )}

        {/* Monthly Leaderboard */}
        {leaderboard && leaderboard.ranking && leaderboard.ranking.length > 0 && (
          <div className="wallet-card mt-4" data-testid="dealer-leaderboard-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-semibold text-white flex items-center gap-2">
                <Trophy size={16} className="text-lojack-clause-400" /> {t.monthlyLeaderboard} — {leaderboard.month}
              </h3>
              <button onClick={() => fetchLeaderboard()} className="text-xs text-gray-500 hover:text-white transition-colors"><RefreshCw size={12} /></button>
            </div>
            <div className="space-y-2">
              {leaderboard.ranking.map((s, i) => {
                const medalStyles = [
                  { bg: 'from-lojack-clause-500/20 to-lojack-clause-600/5', border: 'border-lojack-clause-500/40', badge: 'bg-lojack-clause-500', badgeText: 'text-black', icon: Trophy },
                  { bg: 'from-gray-300/15 to-gray-400/5', border: 'border-gray-400/40', badge: 'bg-gray-300', badgeText: 'text-black', icon: Medal },
                  { bg: 'from-lojack-clause-700/15 to-lojack-clause-800/5', border: 'border-lojack-clause-600/30', badge: 'bg-lojack-clause-700', badgeText: 'text-white', icon: Award },
                ];
                const isPodium = i < 3;
                const m = isPodium ? medalStyles[i] : null;
                const MedalIcon = m?.icon;
                return (
                  <div key={s.id}
                    className={`flex items-center justify-between py-3 px-4 rounded-xl transition-all ${
                      isPodium
                        ? `bg-gradient-to-r ${m.bg} border ${m.border}`
                        : 'bg-[#111] border border-gray-800 hover:border-gray-700'
                    }`}
                    data-testid={`dealer-lb-row-${i}`}>
                    <div className="flex items-center gap-3">
                      {isPodium ? (
                        <div className={`w-8 h-8 rounded-full ${m.badge} flex items-center justify-center`}>
                          <MedalIcon size={14} className={m.badgeText} />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
                          <span className="text-gray-400 text-xs font-bold">{i + 1}°</span>
                        </div>
                      )}
                      <div>
                        <p className={`text-sm font-medium ${isPodium ? 'text-white' : 'text-gray-300'}`}>{s.first_name} {s.last_name}</p>
                        {s.department && <p className="text-gray-600 text-xs">{s.department}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`font-bold tabular-nums ${isPodium ? 'text-white text-lg' : 'text-gray-300 text-sm'}`}>{s.customers_this_month}</p>
                        <p className="text-gray-600 text-[10px]">{t.clients}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold tabular-nums ${isPodium ? 'text-lojack-blue-400' : 'text-gray-500'} text-sm`}>{s.contracts_this_month}</p>
                        <p className="text-gray-600 text-[10px]">{t.contracts}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    ) : (
      <div className="wallet-card text-center py-8">
        <FileBarChart size={40} className="text-gray-700 mx-auto mb-3" />
        <p className="text-gray-500">{t.clickRefresh}</p>
      </div>
    )}
  </div>
);

export default DealerReportView;

DealerReportView.propTypes = {
  sellerReport: PropTypes.object,                         // null until fetchSellerReport resolves
  loadingReport: PropTypes.bool,
  fetchSellerReport: PropTypes.func.isRequired,
  globalTarget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editingGlobalTarget: PropTypes.bool,
  setEditingGlobalTarget: PropTypes.func.isRequired,
  tempGlobalTarget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setTempGlobalTarget: PropTypes.func.isRequired,
  saveGlobalTarget: PropTypes.func.isRequired,
  editingSellerTarget: PropTypes.string,                  // seller.id of the row currently being edited, null otherwise
  setEditingSellerTarget: PropTypes.func.isRequired,
  tempSellerTarget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setTempSellerTarget: PropTypes.func.isRequired,
  saveSellerTarget: PropTypes.func.isRequired,
  leaderboard: PropTypes.object,                          // optional — only renders the section when present
  fetchLeaderboard: PropTypes.func,
  t: PropTypes.object.isRequired,
};
